import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../store.js';
import { ProgressBar } from '../components/ProgressBar.jsx';
import { BusinessInfo } from '../steps/BusinessInfo.jsx';
import { Owners } from '../steps/Owners.jsx';
import { FundingInfo } from '../steps/FundingInfo.jsx';
import { Documents } from '../steps/Documents.jsx';
import { Review } from '../steps/Review.jsx';
import { Sign } from '../steps/Sign.jsx';
import { Centered } from './NotFound.jsx';

const STEPS = ['Business', 'Owners', 'Funding', 'Documents', 'Review', 'Sign'];

export function Apply() {
  const { token } = useParams();
  const nav = useNavigate();
  const { load, loading, pkg, loadError, saving, lastSaved } = useStore((s) => ({
    load: s.load, loading: s.loading, pkg: s.pkg,
    loadError: s.loadError, saving: s.saving, lastSaved: s.lastSaved,
  }));
  const [step, setStep] = useState(0);

  useEffect(() => { load(token); }, [token, load]);

  if (loading) return <Centered title="Loading…"><p>Preparing your application.</p></Centered>;

  if (loadError?.status === 410) {
    return <Centered title="This application is no longer active">
      <p>Please contact your Fundmerica representative.</p>
    </Centered>;
  }
  if (loadError) return <Centered title="Link not found">
    <p>This application link could not be opened.</p>
  </Centered>;

  if (pkg?.status === 'Completed' || pkg?.status === 'Signed') {
    nav(`/completed/${token}`, { replace: true });
    return null;
  }

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <img src="/fundmerica-logo.png" alt="Fundmerica" className="h-8 sm:h-10 w-auto" />
          <div className="text-xs text-slate-500 shrink-0">
            {saving ? 'Saving…' : lastSaved ? `Saved ${timeAgo(lastSaved)}` : 'Funding application'}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6"><ProgressBar step={step} steps={STEPS} /></div>

        <div className="card p-6">
          {step === 0 && <BusinessInfo onNext={next} />}
          {step === 1 && <Owners onNext={next} onBack={back} />}
          {step === 2 && <FundingInfo onNext={next} onBack={back} />}
          {step === 3 && <Documents onNext={next} onBack={back} />}
          {step === 4 && <Review onNext={next} onBack={back} />}
          {step === 5 && <Sign onBack={back} onSigned={() => nav(`/completed/${token}`)} />}
        </div>

        <footer className="text-xs text-slate-400 text-center mt-6">
          <p>This link is unique to your application. Do not share it.</p>
          <p className="mt-1">© Fundmerica · Secure application portal</p>
        </footer>
      </div>
    </div>
  );
}

function timeAgo(d) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return d.toLocaleTimeString();
}
